import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    MdMonitorHeart, MdRefresh, MdBusiness, MdSensors, MdSpeed, MdWarning, MdHelpOutline,
    MdNotificationsOff, MdNotificationsActive, MdExpandMore, MdExpandLess, MdSave, MdSend, MdSearch,
    MdToggleOn, MdToggleOff, MdCheckCircle
} from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';
import RecipientListEditor from '../../components/admin/RecipientListEditor';
import TestEmailResults from '../../components/admin/TestEmailResults';

const REFRESH_MS = 60 * 1000;

// Sorunlu cihazlar listenin başında: kesinti → hiç veri yok → normal → takip devre dışı
const STATUS_ORDER = { gap: 0, never: 1, normal: 2, disabled: 3 };

const STATUS_BADGE = {
    normal: 'badge badge-success',
    gap: 'badge badge-danger',
    never: 'badge badge-warning',
    disabled: 'badge'
};

function useTimeAgo() {
    const { t } = useLanguage();
    return useCallback((date) => {
        if (!date) return '—';
        const mins = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60000));
        if (mins < 1) return t('monitoring.justNow');
        if (mins < 60) return t('monitoring.minutesAgo', { count: mins });
        const hours = Math.floor(mins / 60);
        if (hours < 48) return t('monitoring.hoursAgo', { count: hours });
        return t('monitoring.daysAgo', { count: Math.floor(hours / 24) });
    }, [t]);
}

function StatusBadge({ status }) {
    const { t } = useLanguage();
    return (
        <span className={STATUS_BADGE[status] || STATUS_BADGE.normal} style={{ whiteSpace: 'nowrap' }}>
            {t(`monitoring.status.${status}`)}
        </span>
    );
}

// ── Admin bildirim ayarları ──────────────────────────────────────────────────
function AdminAlertSettings() {
    const { t } = useLanguage();
    const [saved, setSaved] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResults, setTestResults] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/monitoring/settings');
            const d = res.data.data;
            const normalized = {
                enabled: !!d.enabled,
                recipients: d.recipients || [],
                dailySummary: { enabled: !!d.dailySummary?.enabled, hour: d.dailySummary?.hour ?? 8 }
            };
            setSaved(normalized);
            setForm(normalized);
        } catch {
            toast.error(t('alertSettings.loadFailed'));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    if (!form) return <div className="loading-container"><div className="spinner"></div></div>;

    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put('/monitoring/settings', form);
            toast.success(t('alertSettings.saved'));
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || t('alertSettings.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        try {
            setTesting(true);
            setTestResults(null);
            const res = await api.post('/monitoring/settings/test');
            setTestResults(res.data.data);
            const failed = res.data.data.filter(r => !r.success).length;
            failed === 0 ? toast.success(t('alertSettings.testSent')) : toast.error(t('alertSettings.testPartial', { count: failed }));
        } catch (err) {
            toast.error(err.response?.data?.message || t('alertSettings.testFailed'));
        } finally {
            setTesting(false);
        }
    };

    const toggle = (on, onClick) => (
        <button onClick={onClick} disabled={saving}
            style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: 'none',
                cursor: 'pointer', fontWeight: 700, fontSize: 12,
                background: on ? 'rgba(34,197,94,0.12)' : 'var(--bg-body)', color: on ? 'var(--success)' : 'var(--text-muted)'
            }}>
            {on ? <MdNotificationsActive size={16} /> : <MdNotificationsOff size={16} />}
            {on ? t('common.active') : t('common.passive')}
        </button>
    );

    return (
        <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
                <div className="card-title">
                    <div className="card-title-icon" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--warning)' }}>
                        <MdNotificationsActive />
                    </div>
                    {t('monitoring.adminSettingsTitle')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={handleTest}
                        disabled={testing || dirty || form.recipients.length === 0}
                        title={dirty ? t('alertSettings.saveBeforeTest') : ''}>
                        {testing ? <span className="spinner spinner-sm" /> : <MdSend />} {t('alertSettings.sendTest')}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !dirty}>
                        {saving ? <span className="spinner spinner-sm" /> : <MdSave />} {t('common.save')}
                    </button>
                </div>
            </div>
            <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                        <label className="form-label" style={{ margin: 0 }}>{t('monitoring.adminAlerts')}</label>
                        {toggle(form.enabled, () => setForm(f => ({ ...f, enabled: !f.enabled })))}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 16 }}>{t('monitoring.adminAlertsHint')}</div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                        <label className="form-label" style={{ margin: 0 }}>{t('monitoring.dailySummary')}</label>
                        {toggle(form.dailySummary.enabled, () => setForm(f => ({ ...f, dailySummary: { ...f.dailySummary, enabled: !f.dailySummary.enabled } })))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('monitoring.summaryHour')}</span>
                        <select
                            value={form.dailySummary.hour}
                            onChange={(e) => setForm(f => ({ ...f, dailySummary: { ...f.dailySummary, hour: parseInt(e.target.value, 10) } }))}
                            disabled={!form.dailySummary.enabled}
                            style={{ background: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, padding: '4px 6px' }}
                        >
                            {Array.from({ length: 24 }, (_, h) => (
                                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t('monitoring.dailySummaryHint')}</div>
                </div>
                <div>
                    <label className="form-label">{t('monitoring.adminRecipients')}</label>
                    <RecipientListEditor value={form.recipients} onChange={(recipients) => setForm(f => ({ ...f, recipients }))} disabled={saving} />
                    <TestEmailResults results={testResults} />
                </div>
            </div>
        </div>
    );
}

// ── Sistem Kontrolü sayfası ────────────────────────────────────────────────────
// Solda firma listesi (kesintisi olanlar üstte), sağda seçili firmanın sensör/sayaç tablosu.
export default function AdminMonitoringPage() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const timeAgo = useTimeAgo();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [companySearch, setCompanySearch] = useState('');
    const [deviceSearch, setDeviceSearch] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [trackingId, setTrackingId] = useState(null);

    const fetchStatus = useCallback(async (silent) => {
        try {
            if (!silent) setLoading(true);
            const res = await api.get('/monitoring/status');
            setData(res.data.data);
        } catch {
            if (!silent) toast.error(t('monitoring.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchStatus();
        const timer = setInterval(() => fetchStatus(true), REFRESH_MS);
        return () => clearInterval(timer);
    }, [fetchStatus]);

    const toggleTracking = async (device) => {
        setTrackingId(device._id);
        try {
            await api.put('/monitoring/tracking', { kind: device.kind, id: device._id, enabled: !device.trackingEnabled });
            toast.success(!device.trackingEnabled ? t('alertSettings.trackingEnabled') : t('alertSettings.trackingDisabled'));
            await fetchStatus(true);
        } catch (err) {
            toast.error(err.response?.data?.message || t('alertSettings.trackingFailed'));
        } finally {
            setTrackingId(null);
        }
    };

    // Sunucu firmaları zaten "kesintisi olanlar önce, sonra ada göre" sıralı gönderir.
    const companies = useMemo(() => {
        if (!data) return [];
        const q = companySearch.trim().toLocaleLowerCase('tr');
        return q ? data.companies.filter(c => c.name.toLocaleLowerCase('tr').includes(q)) : data.companies;
    }, [data, companySearch]);

    const selected = useMemo(() => {
        if (!data) return null;
        return data.companies.find(c => c._id === selectedId) || companies[0] || null;
    }, [data, companies, selectedId]);

    const devices = useMemo(() => {
        if (!selected) return [];
        const q = deviceSearch.trim().toLocaleLowerCase('tr');
        return [...selected.devices]
            .filter(d => !q || d.name.toLocaleLowerCase('tr').includes(q) || (d.systemName || '').toLocaleLowerCase('tr').includes(q))
            .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name, 'tr'));
    }, [selected, deviceSearch]);

    const selectCompany = (id) => {
        setSelectedId(id);
        setDeviceSearch('');
    };

    const totals = data?.totals;
    const stat = (i, icon, bg, color, value, label) => (
        <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }} className="stat-card">
            <div className="stat-icon" style={{ background: bg, color }}>{icon}</div>
            <div className="stat-info">
                <div className="stat-value">{loading && !data ? '—' : value}</div>
                <div className="stat-label">{label}</div>
            </div>
        </motion.div>
    );

    const counts = (c) => {
        const n = { normal: 0, problem: 0 };
        c.devices.forEach(d => { if (d.status === 'gap' || d.status === 'never') n.problem++; else if (d.status === 'normal') n.normal++; });
        return n;
    };

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-title">{t('monitoring.title')}</h1>
                    <p className="page-subtitle">
                        {t('monitoring.subtitle')}
                        {data && <> · {t('monitoring.updated', { time: new Date(data.generatedAt).toLocaleTimeString() })}</>}
                    </p>
                </div>
                <div className="page-header-actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowSettings(s => !s)}>
                        <MdNotificationsActive /> {t('monitoring.adminSettingsTitle')} {showSettings ? <MdExpandLess /> : <MdExpandMore />}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => fetchStatus()}>
                        <MdRefresh /> {t('common.refresh')}
                    </button>
                </div>
            </div>

            {showSettings && <AdminAlertSettings />}

            <div className="stats-grid">
                {stat(0, <MdBusiness />, 'rgba(79,142,247,0.15)', 'var(--primary)', totals?.companies, t('monitoring.totalCompanies'))}
                {stat(1, <MdSensors />, 'rgba(139,92,246,0.15)', 'var(--secondary)', totals?.sensors, t('monitoring.totalSensors'))}
                {stat(2, <MdSpeed />, 'rgba(245,158,11,0.15)', 'var(--warning)', totals?.counters, t('monitoring.totalCounters'))}
                {stat(3, <MdWarning />, 'rgba(239,68,68,0.15)', 'var(--danger)', totals?.gap, t('monitoring.status.gap'))}
                {stat(4, <MdHelpOutline />, 'rgba(245,158,11,0.15)', 'var(--warning)', totals?.never, t('monitoring.status.never'))}
                {stat(5, <MdNotificationsOff />, 'rgba(148,163,184,0.15)', 'var(--text-muted)', totals?.disabled, t('monitoring.status.disabled'))}
            </div>

            {loading && !data ? (
                <div className="loading-container"><div className="spinner"></div></div>
            ) : (
                <div className="detail-grid-2col" style={{ alignItems: 'start' }}>
                    {/* Firma listesi */}
                    <div className="card monitor-company-panel">
                        <div className="card-header">
                            <div className="card-title">
                                <div className="card-title-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                                    <MdBusiness />
                                </div>
                                {t('monitoring.companies')}
                            </div>
                            {totals && totals.affectedCompanies > 0 && (
                                <span className="badge badge-danger">{t('monitoring.affectedShort', { count: totals.affectedCompanies })}</span>
                            )}
                        </div>
                        <div style={{ padding: '12px 12px 0', position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 24, top: 'calc(50% + 6px)', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 16 }}>
                                <MdSearch />
                            </span>
                            <input
                                className="form-input"
                                style={{ paddingLeft: 36 }}
                                placeholder={t('monitoring.searchCompany')}
                                value={companySearch}
                                onChange={(e) => setCompanySearch(e.target.value)}
                            />
                        </div>
                        <div className="monitor-company-list">
                            {companies.length === 0 ? (
                                <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 4px' }}>{t('monitoring.noResults')}</div>
                            ) : companies.map(c => {
                                const n = counts(c);
                                const active = selected && selected._id === c._id;
                                return (
                                    <button
                                        key={c._id}
                                        onClick={() => selectCompany(c._id)}
                                        className={`monitor-company-item${active ? ' active' : ''}`}
                                    >
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: n.problem > 0 ? 'var(--danger)' : 'var(--success)' }} />
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                                        {n.problem > 0
                                            ? <span className="badge badge-danger" style={{ padding: '2px 8px' }}><MdWarning /> {n.problem}</span>
                                            : <span className="badge badge-success" style={{ padding: '2px 8px' }}><MdCheckCircle /> {n.normal}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Seçili firmanın sensörleri */}
                    <div className="card">
                        {!selected ? (
                            <div className="empty-state">
                                <div className="empty-state-desc">{t('monitoring.noResults')}</div>
                            </div>
                        ) : (
                            <>
                                <div className="card-header monitor-detail-header" style={{ flexWrap: 'wrap' }}>
                                    <div className="card-title">
                                        <div className="card-title-icon" style={{
                                            background: selected.alertingCount > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                                            color: selected.alertingCount > 0 ? 'var(--danger)' : 'var(--success)'
                                        }}>
                                            <MdMonitorHeart />
                                        </div>
                                        <div>
                                            <div>{selected.name}</div>
                                            <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
                                                {t('monitoring.staleLimit', { count: selected.staleMinutes })}
                                                {' · '}
                                                {selected.alertingCount > 0
                                                    ? t('monitoring.alertingSensors', { count: selected.alertingCount })
                                                    : t('monitoring.noOutage')}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <input
                                            className="form-input"
                                            style={{ width: 200, height: 34 }}
                                            placeholder={t('monitoring.searchSensor')}
                                            value={deviceSearch}
                                            onChange={(e) => setDeviceSearch(e.target.value)}
                                        />
                                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/companies/${selected._id}`)}>
                                            {t('monitoring.openCompany')}
                                        </button>
                                    </div>
                                </div>
                                {devices.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-desc">{t('monitoring.noDevices')}</div>
                                    </div>
                                ) : (
                                    <div className="table-container">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>{t('monitoring.system')}</th>
                                                    <th>{t('monitoring.device')}</th>
                                                    <th>{t('monitoring.type')}</th>
                                                    <th>{t('monitoring.lastData')}</th>
                                                    <th>{t('monitoring.statusLabel')}</th>
                                                    <th style={{ textAlign: 'center' }}>{t('monitoring.tracking')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {devices.map(d => (
                                                    <tr key={d._id}>
                                                        <td>{d.systemName || '—'}</td>
                                                        <td>
                                                            <button
                                                                onClick={() => navigate(d.kind === 'counter' ? `/admin/counters/${d.uuid}` : `/admin/sensors/${d.uuid}`)}
                                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: 14, textAlign: 'left' }}
                                                            >
                                                                {d.name}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            {d.kind === 'counter'
                                                                ? <span className="badge badge-warning"><MdSpeed /> {t('common.counter')}</span>
                                                                : <span className="badge badge-primary"><MdSensors /> {t('common.sensor')}</span>}
                                                        </td>
                                                        <td title={d.lastDataAt ? new Date(d.lastDataAt).toLocaleString() : ''}>
                                                            {d.lastDataAt ? (
                                                                <>
                                                                    <div style={{ color: 'var(--text-primary)' }}>{timeAgo(d.lastDataAt)}</div>
                                                                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{new Date(d.lastDataAt).toLocaleString()}</div>
                                                                </>
                                                            ) : '—'}
                                                        </td>
                                                        <td><StatusBadge status={d.status} /></td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                onClick={() => toggleTracking(d)}
                                                                disabled={trackingId === d._id}
                                                                title={d.trackingEnabled ? t('alertSettings.trackingOnTitle') : t('alertSettings.trackingOffTitle')}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', margin: '0 auto' }}
                                                            >
                                                                {d.trackingEnabled
                                                                    ? <MdToggleOn size={26} style={{ color: 'var(--success)' }} />
                                                                    : <MdToggleOff size={26} style={{ color: 'var(--text-muted)' }} />}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
